import { type Builder, TAGS } from "../../types.js";
import { isDefined } from "../../../../utils/filters/isDefined.js";
import type {
	LegalEntity,
	LegalEntityHierarchy,
	LegalEntityHierarchyResponse,
	UpdateHierarchyRequestBody,
} from "./types.js";

/**
 * `UpdateHierarchyRequestBody` only carries ids (the wire shape); the
 * optimistic cache patch needs full `LegalEntity` objects (names) to render
 * immediately, so this rebuilds the tree from the already-cached
 * `getLegalEntities` list rather than waiting on the server's response.
 */
const toOptimisticLegalEntityHierarchy = (
	node: UpdateHierarchyRequestBody,
	legalEntityById: Map<string, LegalEntity>,
): LegalEntityHierarchy | null => {
	const legalEntity = legalEntityById.get(node.legalEntityId);
	const children = node.children.map((child) =>
		toOptimisticLegalEntityHierarchy(child, legalEntityById),
	);
	if (!legalEntity || children.some((child) => child === null)) {
		return null;
	}
	return {
		legalEntity,
		children: children.filter(isDefined),
	};
};

export const buildCompanyDomainEndpoints = (builder: Builder) => ({
	getLegalEntities: builder.query<LegalEntity[], string>({
		query: (companyDomainId) =>
			`/v1/company-domain/${companyDomainId}/legal-entity`,
		providesTags: (result) => {
			if (result) {
				return [
					"LegalEntity",
					...result.map((legalEntity) => ({
						type: "LegalEntity" as const,
						id: legalEntity.id,
					})),
				];
			}
			return ["LegalEntity"];
		},
	}),

	getLegalEntity: builder.query<
		LegalEntity,
		{ companyDomainId: string; legalEntityId: string }
	>({
		query: ({ companyDomainId, legalEntityId }) =>
			`/v1/company-domain/${companyDomainId}/legal-entity/${legalEntityId}`,
		providesTags: (_result, error, arg) => {
			if (error) {
				return ["LegalEntity"];
			}
			return [
				"LegalEntity",
				{
					type: "LegalEntity",
					id: arg.legalEntityId,
				},
			];
		},
	}),

	upsertLegalEntityFromFusionhubIntegrations: builder.mutation<
		LegalEntity[],
		{ companyDomainId: string; integrationIds: number[] }
	>({
		query: ({ companyDomainId, integrationIds }) => ({
			url: `/v1/company-domain/${companyDomainId}/legal-entity/integration/fusionhub`,
			method: "PUT",
			body: {
				fusionHubIntegrationIds: integrationIds,
			},
		}),
		invalidatesTags: (_, err) => {
			if (err) {
				return [];
			}

			return TAGS;
		},
	}),

	deleteLegalEntity: builder.mutation<
		void,
		{ companyDomainId: string; legalEntityId: string }
	>({
		query: ({ companyDomainId, legalEntityId }) => ({
			url: `/v1/company-domain/${companyDomainId}/legal-entity/${legalEntityId}`,
			method: "DELETE",
		}),
		invalidatesTags: () => TAGS,
	}),

	getCompanyHierarchy: builder.query<LegalEntityHierarchyResponse, string>({
		query: (companyDomainId) =>
			`/v1/company-domain/${companyDomainId}/hierarchy`,
		providesTags: () => ["LegalEntityStructure"],
	}),

	updateCompanyHierarchy: builder.mutation<
		LegalEntityHierarchyResponse,
		{ companyDomainId: string; hierarchy: UpdateHierarchyRequestBody }
	>({
		query: ({ companyDomainId, hierarchy }) => ({
			url: `/v1/company-domain/${companyDomainId}/hierarchy`,
			method: "PUT",
			body: hierarchy,
		}),
		invalidatesTags: () => TAGS,
		async onQueryStarted(
			{ companyDomainId, hierarchy },
			{ dispatch, getState, queryFulfilled },
		) {
			// Lazy import: nexusApi.ts imports this file to build itself, so a static import would cycle.
			const { nexusApi } = await import("../../nexusApi.js");
			const legalEntities =
				nexusApi.endpoints.getLegalEntities.select(companyDomainId)(getState())
					.data ?? [];
			const legalEntityById = new Map(
				legalEntities.map((entity) => [entity.id, entity]),
			);
			const optimisticHierarchy = toOptimisticLegalEntityHierarchy(
				hierarchy,
				legalEntityById,
			);
			if (!optimisticHierarchy) {
				// getLegalEntities cache missing/stale for one of the ids — skip the
				// optimistic patch and let the real response update the UI instead.
				return;
			}

			const patch = dispatch(
				nexusApi.util.updateQueryData(
					"getCompanyHierarchy",
					companyDomainId,
					(draft) => {
						draft.hierarchy = optimisticHierarchy;
					},
				),
			);
			try {
				await queryFulfilled;
			} catch {
				patch.undo();
			}
		},
	}),

	removeCompanyHierarchy: builder.mutation<
		LegalEntityHierarchyResponse,
		{ companyDomainId: string }
	>({
		query: ({ companyDomainId }) => ({
			url: `/v1/company-domain/${companyDomainId}/hierarchy`,
			method: "DELETE",
		}),
		invalidatesTags: () => TAGS,
		async onQueryStarted({ companyDomainId }, { dispatch, queryFulfilled }) {
			const { nexusApi } = await import("../../nexusApi.js");
			const patch = dispatch(
				nexusApi.util.updateQueryData(
					"getCompanyHierarchy",
					companyDomainId,
					(draft) => {
						draft.hierarchy = null;
					},
				),
			);
			try {
				await queryFulfilled;
			} catch {
				patch.undo();
			}
		},
	}),

	getCompanyDomains: builder.query<any, any>({
		query() {
			return "/v1/company-domain";
		},
	}),
});
